// Theme store — dark / light / system. Persisted separately from the
// game save ('pm-theme') so theme survives new games. The anti-FOUC
// script in index.html reads the same key before first paint.

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeMode = 'dark' | 'light' | 'system'
export type ResolvedTheme = 'dark' | 'light'

// Dark mode is forced while the broadcast art direction settles; flip this
// off (and restore the theme picker in SettingsTab + the anti-FOUC script
// in index.html) to re-enable the light theme.
const FORCE_DARK = true

interface ThemeState {
  mode: ThemeMode
  resolved: ResolvedTheme
  setMode: (mode: ThemeMode) => void
}

function systemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark'
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (FORCE_DARK) return 'dark'
  return mode === 'system' ? systemTheme() : mode
}

function applyTheme(resolved: ResolvedTheme) {
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = resolved
  }
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      resolved: resolve('system'),

      setMode: (mode) => {
        const resolved = resolve(mode)
        applyTheme(resolved)
        set({ mode, resolved })
      },
    }),
    {
      name: 'pm-theme',
      partialize: (state) => ({ mode: state.mode }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const resolved = resolve(state.mode)
          applyTheme(resolved)
          // resolved is derived, recompute after rehydration
          useThemeStore.setState({ resolved })
        }
      },
    }
  )
)

// Follow OS theme changes while in 'system' mode (skipped while dark is forced)
if (!FORCE_DARK && typeof window !== 'undefined' && window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
    const { mode } = useThemeStore.getState()
    if (mode === 'system') {
      const resolved = systemTheme()
      applyTheme(resolved)
      useThemeStore.setState({ resolved })
    }
  })
}
